
import { useState, useEffect, useRef } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function CareersPage() {
  const [formVisible, setFormVisible] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    specialization: '',
    experience: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setFormVisible(true);
      },
      { threshold: 0.1 }
    );
    if (formRef.current) observer.observe(formRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.phone || !formData.city || !formData.specialization) {
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('loading');

    try {
      const formBody = new URLSearchParams({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        specialization: formData.specialization,
        experience: formData.experience,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6fcfdan5jm489bu5h3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', phone: '', city: '', specialization: '', experience: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  const benefits = [
    { icon: 'ri-time-line', title: 'مرونة في العمل', description: 'اختر أوقات العمل التي تناسبك' },
    { icon: 'ri-money-dollar-circle-line', title: 'دخل مجزي', description: 'عوائد مالية تنافسية ومحفزة' },
    { icon: 'ri-graduation-cap-line', title: 'تطوير مهني', description: 'فرص تدريب وتطوير مستمرة' },
    { icon: 'ri-team-line', title: 'بيئة داعمة', description: 'فريق متعاون ومحفز' },
    { icon: 'ri-map-pin-line', title: 'تغطية واسعة', description: 'فرص عمل في جميع مناطق المملكة' },
    { icon: 'ri-heart-pulse-line', title: 'أثر إيجابي', description: 'ساهم في تحسين حياة الآخرين' },
  ];

  const cities = ['الرياض', 'جدة', 'الخبر', 'الدمام', 'خميس مشيط', 'أبها', 'المدينة المنورة', 'مكة المكرمة', 'تبوك', 'أخرى'];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#184187] to-[#2563a8] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center text-white">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6 animate-[fadeInUp_0.6s_ease-out]">
              <span className="text-white text-sm font-medium">انضم لفريقنا</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-[fadeInUp_0.8s_ease-out]">فرص العمل</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed animate-[fadeInUp_1s_ease-out]">
              مطلوب أخصائيين سمع في جميع مناطق المملكة
            </p>
          </div>
        </div>
      </section>

      {/* Job Description */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">نبحث عنك</h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
              إذا كنت أخصائي سمع شغوف بتخصصك، حديث تخرج أو على رأس العمل، وتبحث عن بيئة عمل مرنة ومحفزة، فأنت من أعضاء فريق سماعتي. نحتاج أخصائيين في: <strong>الرياض – جدة – خميس مشيط – الخبر</strong> وجميع مناطق المملكة.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 hover:bg-[#184187] hover:text-white group transition-all duration-500 hover:-translate-y-2 hover:shadow-xl cursor-default"
              >
                <div className="w-14 h-14 bg-[#184187]/10 group-hover:bg-white/20 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500">
                  <i className={`${benefit.icon} text-3xl text-[#184187] group-hover:text-white transition-colors duration-500`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white mb-2 transition-colors duration-500">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 group-hover:text-white/90 leading-relaxed transition-colors duration-500">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section ref={formRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div
              className={`text-center mb-12 transition-all duration-1000 ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">قدّم طلبك الآن</h2>
              <p className="text-lg text-gray-600">
                املأ النموذج التالي وسنتواصل معك في أقرب وقت
              </p>
            </div>

            <div
              className={`bg-white rounded-2xl shadow-xl p-8 md:p-12 transition-all duration-1000 delay-200 ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <form id="careers-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                      الاسم الكامل <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-sm"
                      placeholder="أدخل اسمك الكامل"
                    />
                  </div>

                  <div>
                    <label htmlFor="career-email" className="block text-sm font-semibold text-gray-700 mb-2">
                      البريد الإلكتروني <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="career-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-sm"
                      placeholder="example@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="career-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      رقم الجوال <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="career-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-sm"
                      placeholder="05xxxxxxxx"
                    />
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                      المدينة <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-sm bg-white cursor-pointer"
                    >
                      <option value="">اختر المدينة</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="specialization" className="block text-sm font-semibold text-gray-700 mb-2">
                      التخصص <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="specialization"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-sm bg-white cursor-pointer"
                    >
                      <option value="">اختر التخصص</option>
                      <option value="أخصائي سمعيات">أخصائي سمعيات</option>
                      <option value="أخصائي سمع واتزان">أخصائي سمع واتزان</option>
                      <option value="فني سماعات طبية">فني سماعات طبية</option>
                      <option value="أخرى">أخرى</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                      سنوات الخبرة
                    </label>
                    <select
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-sm bg-white cursor-pointer"
                    >
                      <option value="">اختر سنوات الخبرة</option>
                      <option value="حديث تخرج">حديث تخرج</option>
                      <option value="1-3 سنوات">1-3 سنوات</option>
                      <option value="3-5 سنوات">3-5 سنوات</option>
                      <option value="5-10 سنوات">5-10 سنوات</option>
                      <option value="أكثر من 10 سنوات">أكثر من 10 سنوات</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="career-message" className="block text-sm font-semibold text-gray-700 mb-2">
                    نبذة عنك
                  </label>
                  <textarea
                    id="career-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#184187] focus:ring-2 focus:ring-[#184187]/20 outline-none transition-all text-sm resize-none"
                    placeholder="اكتب نبذة مختصرة عن خبراتك ومؤهلاتك (حد أقصى 500 حرف)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 حرف</p>
                </div>

                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className="w-full bg-[#184187] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#0f2d6b] transition-all hover:shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitStatus === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      جاري الإرسال...
                    </span>
                  ) : (
                    'إرسال الطلب'
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg text-center flex items-center justify-center gap-2">
                    <i className="ri-checkbox-circle-fill text-xl"></i>
                    تم إرسال طلبك بنجاح! سنتواصل معك قريباً.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg text-center flex items-center justify-center gap-2">
                    <i className="ri-error-warning-fill text-xl"></i>
                    يرجى ملء جميع الحقول المطلوبة والمحاولة مرة أخرى.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
